import { Panel } from "../../../../components/panels/Panel";
import { InputSelect } from "../../../../components/forms/inputs/InputSelect";
import { BookmarkIcon, PlayIcon } from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import { useOrgData } from "../../../../contexts/OrgDataContext";
import { useErgonomicsContext } from "../../../../contexts/ErgonomicsContext";
import { ErgonomicsColoredScore } from "./ErgonomicsCardData";
import { Ergonomics5Risks } from "../Ergonomics5Risks";
import { BookmarkIcon as BookmarkIconOutline } from "@heroicons/react/24/outline";
import { saveLayoutData } from "../../../../services/ergonomics/ergonomics-service";
import { formatSecondsToFullDate, getCurrentTimestamp } from "../../../../helpers/date/date-helper";

const START_SHIFT_1 = 0;
const START_SHIFT_2 = 12;

const getCurrentShift = (delta_days = 0, now = new Date()) => {
  const date = new Date(now);
  date.setDate(now.getDate() + delta_days);

  const hour = date.getHours();
  const isShift1 = hour >= START_SHIFT_1 && hour < START_SHIFT_2;

  const start = new Date(date);
  start.setHours(isShift1 ? START_SHIFT_1 : START_SHIFT_2, 0, 0, 0);

  const stop = new Date(date);
  stop.setHours(isShift1 ? START_SHIFT_2 : 24, 0, 0, 0);

  return { start: start.getTime(), stop: stop.getTime() };
};

const getShiftUntilNow = (delta_days = 0, now = new Date()) => {
  const date = new Date(now);
  date.setDate(now.getDate() + delta_days);

  const hour = date.getHours();
  const isShift1 = hour < 12;

  const start = new Date(date);
  start.setHours(isShift1 ? 0 : 12, 0, 0, 0);

  const stop = new Date(date);

  return { start: start.getTime(), stop: stop.getTime() };
};

const getPreviousShift = (now = new Date()) => {
  const date = new Date(now);
  const hour = date.getHours();

  let start, stop;

  if (hour >= START_SHIFT_1 && hour < START_SHIFT_2) {
    const prevDay = new Date(date);
    prevDay.setDate(date.getDate() - 1);

    start = new Date(prevDay);
    start.setHours(START_SHIFT_2, 0, 0, 0);

    stop = new Date(prevDay);
    stop.setHours(24, 0, 0, 0);
  } else {
    start = new Date(date);
    start.setHours(START_SHIFT_1, 0, 0, 0);

    stop = new Date(date);
    stop.setHours(START_SHIFT_2, 0, 0, 0);
  }

  return { start: start.getTime(), stop: stop.getTime() };
};

const getCurrentDay = (delta_days = 0, now = new Date()) => {
  const date = new Date(now);
  date.setDate(now.getDate() + delta_days);

  const start = new Date(date);
  start.setHours(0, 0, 0, 0);

  const stop = new Date(start);
  stop.setHours(24, 0, 0, 0);

  return { start: start.getTime(), stop: stop.getTime() };
};

const getCurrentWeek = (delta_weeks = 0, now = new Date()) => {
  const date = new Date(now);
  date.setDate(date.getDate() + delta_weeks * 7);

  const day = date.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;

  const start = new Date(date);
  start.setDate(date.getDate() + diffToMonday);
  start.setHours(0, 0, 0, 0);

  const stop = new Date(start);
  stop.setDate(start.getDate() + 7);
  stop.setHours(0, 0, 0, 0);

  return { start: start.getTime(), stop: stop.getTime() };
};

const COMPARE_CRITERIA = [
  {id: 1, label: 'LA MOYENNE REBA', value: 'reba_mean'},
  {id: 2, label: 'L\'INDICE DE FATIGUE', value: 'reba_fatigue_index'},
  {id: 3, label: 'LA REPARTITION PAR NIVEAU DE RISQUE', value: 'risk_repartition_pct'},
];

const COMPARE_SOURCE = [
  {id: 1, label: 'L\'ÉQUIPE ACTUELLE', ...getCurrentShift()},
  {id: 2, label: 'LA JOURNEE ACTUELLE', ...getCurrentDay()},
  {id: 3, label: 'LA SEMAINE ACTUELLE', ...getCurrentWeek()},
];

const COMPARE_TO = [
  {id: 1, label: 'LA MÊME EQUIPE, HIER', ...getCurrentShift(-1)},
  {id: 2, label: 'LA MÊME EQUIPE, HIER, JUSQU\'À LA MÊME HEURE', ...getShiftUntilNow(-1)},
  {id: 3, label: 'L\'ÉQUIPE PRÉCÉDENTE', ...getPreviousShift()},
  {id: 4, label: 'LE JOUR PRÉCÉDENT', ...getCurrentDay(-1)},
  {id: 5, label: 'LE MÊME JOUR, LA SEMAINE DERNIÈRE', ...getCurrentDay(-7)},
  {id: 7, label: 'LA SEMAINE ACTUELLE', ...getCurrentWeek()},
  {id: 6, label: 'LA SEMAINE DERNIÈRE', ...getCurrentWeek(-1)},
];

export const ErgonomicsCardDataRequest = ({ showWorkstationSelector }) => {

  const { workstations, workstationSelected } = useOrgData();
  const [localWorkstationSelected, setLocalWorkstationSelected] = useState(null);
  const { filters, layoutData, setLayoutData } = useErgonomicsContext();

  useEffect(() => {
    setLocalWorkstationSelected(workstationSelected);
  }, [workstationSelected]);

  const POSSIBLE_PHASES = filters.isLoading
    ? [{ id: -1, name: null, display_text: 'TOUTES LES PHASES' }]
    : [{ id: -1, name: null, display_text: 'TOUTES LES PHASES' }, ...filters.data];

  const [criteria, setCriteria] = useState(COMPARE_CRITERIA[0]);
  const [source, setSource] = useState(COMPARE_SOURCE[0]);
  const [to, setTo] = useState(COMPARE_TO[0]);
  const [phase, setPhase] = useState(POSSIBLE_PHASES[0]);
  const [results, setResults] = useState(null);
  const [displayedCriteria, setDisplayedCriteria] = useState(COMPARE_CRITERIA[0]);
  const [displayedSource, setDisplayedSource] = useState(COMPARE_SOURCE[0]);
  const [displayedTo, setDisplayedTo] = useState(COMPARE_TO[0]);

  const sendRequest = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_ADDRESS + 'request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          request_name: criteria.value,
          start_1: Math.floor(source.start / 1000),
          stop_1: Math.floor(source.stop / 1000),
          start_2: Math.floor(to.start / 1000),
          stop_2: Math.floor(to.stop / 1000),
          client_id: localWorkstationSelected?.device?.stream_id,
          phase: phase?.name,
        }),
      });

      const data = await response.json();
      setResults(data);
      setDisplayedCriteria(criteria);
      setDisplayedSource(source);
      setDisplayedTo(to);

    } catch (error) {
      console.error('Error while requesting data.', error);
      alert('Error while requesting data.');
    }
  };

  const addBookmark = (newBookmark) => {
    const newLayoutData = { ...layoutData, bookmarks: [...(layoutData?.bookmarks || []), { ...newBookmark, time: getCurrentTimestamp() }] };
    setLayoutData(newLayoutData);
    saveLayoutData(newLayoutData);
    setResults(null);
  };

  const removeBookmark = (bookmarkIndex) => {
    const newLayoutData = {
      ...layoutData,
      bookmarks: layoutData?.bookmarks?.filter((_, i) => i !== bookmarkIndex) || []
    };
    setLayoutData(newLayoutData);
    saveLayoutData(newLayoutData);
  };

  const FormattedResults = ({ displayedSource, displayedTo, displayedCriteria, results, time=null, bookmarkIndex=null }) => {
    return (
      <div className="h-full max-h-20 flex w-full gap-4">
        {
          results.delta.length === 1 ?
            <>
              <div className="h-full flex flex-col justify-center items-center text-xs">
                {
                  bookmarkIndex === null ?
                    <BookmarkIconOutline className="size-6 cursor-pointer" onClick={() => addBookmark({
                      displayedSource, displayedTo, displayedCriteria, results
                    })}/> :
                    <BookmarkIcon className="size-6 cursor-pointer" onClick={() => removeBookmark(bookmarkIndex)}/>
                }
                {
                  time && <>{formatSecondsToFullDate(time)}</>
                }
              </div>
              <div className="w-full bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
                <ErgonomicsColoredScore score={results.value_1} type={displayedCriteria.value === 'reba_mean' ? 'reba' : 'index'}/>
                <div className="text-sm">Sur {displayedSource.label.toLowerCase()}</div>
              </div>
              <div className="w-full bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
                <ErgonomicsColoredScore score={results.value_2} type={displayedCriteria.value === 'reba_mean' ? 'reba' : 'index'}/>
                <div className="text-sm">Sur {displayedTo.label.toLowerCase()}</div>
              </div>
              <div className="w-full bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
                <ErgonomicsColoredScore score={`${results.delta_pct >= 0 ? '+' : ''}${Math.floor(results.delta_pct * 10) / 10}%`} type={'other'}/>
                <div className="text-sm">Tendance</div>
              </div>
            </> :
            <>
              <div className="w-1/3 bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
                {/*<ErgonomicsColoredScore score={results.value_1} type={displayedCriteria.value === 'reba_mean' ? 'reba' : 'index'}/>*/}
                <Ergonomics5Risks bins={results.value_1}/>
                <div className="text-sm">Sur {displayedSource.label.toLowerCase()}</div>
              </div>
              <div className="w-1/3 bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
                {/*<ErgonomicsColoredScore score={results.value_2} type={displayedCriteria.value === 'reba_mean' ? 'reba' : 'index'}/>*/}
                <Ergonomics5Risks bins={results.value_2}/>
                <div className="text-sm">Sur {displayedTo.label.toLowerCase()}</div>
              </div>
              <div className="w-1/3 bg-perception-black-700 rounded-md p-4 flex flex-col items-center justify-center text-center gap-2">
                {/*<ErgonomicsColoredScore score={`${-results.delta_pct >= 0 ? '+' : ''}${Math.floor(-results.delta_pct * 10) / 10}%`} type={'other'}/>*/}
                <Ergonomics5Risks bins={results.delta_pct} mode="delta"/>
                <div className="text-sm">Tendance</div>
              </div>
            </>
        }
      </div>
    );
  };

  return (
    <Panel title="EXPLORATEUR/COMPARATEUR DE DONNEES">
      <div className="h-full flex flex-col">
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 items-center">
          <div className="text-right">Je souhaite comparer</div>
          <InputSelect
            items={COMPARE_CRITERIA}
            onChange={setCriteria}
            itemToString={(item) => item?.label}
            defaultValue={COMPARE_CRITERIA[0]}
          />

          <div className="text-right">entre</div>
          <InputSelect
            items={COMPARE_SOURCE}
            onChange={setSource}
            itemToString={(item) => item?.label}
            defaultValue={COMPARE_SOURCE[0]}
          />

          <div className="text-right">et</div>
          <InputSelect
            items={COMPARE_TO}
            onChange={setTo}
            itemToString={(item) => item?.label}
            defaultValue={COMPARE_TO[0]}
          />

          <div className="text-right">pour</div>
          <InputSelect
            items={POSSIBLE_PHASES}
            onChange={setPhase}
            itemToString={(item) => item?.display_text.toUpperCase()}
            defaultValue={POSSIBLE_PHASES[0]}
          />

          {
            showWorkstationSelector &&
              <>
                <div className="text-right">sur le poste</div>
                <InputSelect
                  items={workstations}
                  onChange={setLocalWorkstationSelected}
                  itemToString={(item) => item?.name}
                  defaultValue={localWorkstationSelected}
                />
              </>
          }
        </div>

        <div>
          {
            results && results?.delta.length > 0 ?
              <div className="h-full flex flex-col justify-center items-center gap-4 mt-4">
                <div>
                  <button className="bg-perception-blue text-white px-4 py-2 rounded-md flex items-center" onClick={sendRequest}>
                    <PlayIcon aria-hidden="true" className="size-5 mr-2" /> Comparer
                  </button>
                </div>
                <FormattedResults
                  displayedSource={displayedSource}
                  displayedTo={displayedTo}
                  displayedCriteria={displayedCriteria}
                  results={results}
                />
              </div>
              :
              <div className="h-full flex flex-col justify-center items-center text-perception-gray-800 text-center gap-4">
                {/*<ArrowTrendingUpIcon className='w-24 h-24'/>*/}
                <button className="bg-perception-blue text-white px-4 py-2 rounded-md flex items-center mt-4" onClick={sendRequest}>
                  <PlayIcon aria-hidden="true" className="size-5 mr-2" /> Comparer
                </button>
                {
                  results?.delta.length === 0 ?
                    <p>Aucun résultat pour les critères sélectionnés.<br/>Lancez une autre comparaison pour obtenir les résultats.</p>
                    : <p>Lancez une comparaison pour obtenir les résultats.</p>
                }
              </div>
          }
        </div>
        <div>
          {
            layoutData?.bookmarks?.sort((a, b) => b.time - a.time).map((bookmark, bookmarkIndex) => (
              <div className="mt-4 h-20" key={bookmarkIndex}>
                <FormattedResults
                  displayedSource={bookmark.displayedSource}
                  displayedTo={bookmark.displayedTo}
                  displayedCriteria={bookmark.displayedCriteria}
                  results={bookmark.results}
                  bookmarkIndex={bookmarkIndex}
                  time={bookmark.time}
                />
              </div>
            ))
          }
        </div>
      </div>
    </Panel>
  );
};